import { useEffect, useState, FC, useMemo } from "react";
import _, { Dictionary } from "lodash";
import stringify from "fast-json-stable-stringify";

export type NumericWeight = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;
export const NUMERIC_WEIGHTS = Object.freeze(_.times(9, (index) => (index * 100) as NumericWeight));

export type GoogleFontWeight = NumericWeight | "italic" | "i" | "bold" | "b" | "bolditalic" | "bi";

export type GoogleFont = {
    name: string;
    weights?: GoogleFontWeight[];
};

export type GoogleFontDisplay = "auto" | "block" | "swap" | "fallback" | "optional";

export type GoogleFontLoaderProps = {
    fonts: GoogleFont[];
    display?: GoogleFontDisplay;
};

type GoogleFontLinkElement = HTMLLinkElement | null;

const weightDictionary: Record<string, string | number | undefined> = { italic: "i", bold: "b", bolditalic: "bi" };

const unifyWeight = (font: GoogleFontWeight): Extract<GoogleFontWeight, "i" | "b" | "bi"> | string =>
    `${weightDictionary[font] ?? font}`;

type Weight = ReturnType<typeof unifyWeight>;
type Font = { name: string; weights: Set<Weight> };

const mergedFonts = (fonts: GoogleFont[]): Font[] => {
    const fontNameMap: Dictionary<Font> = {};
    fonts.forEach(({ name, weights = [] }) => {
        const family = name.replace(/ +/g, "+");

        const font = fontNameMap[family] ?? { name: family, weights: new Set() };
        fontNameMap[family] = font;

        weights.forEach((weight) => font.weights.add(unifyWeight(weight)));
    });

    return Object.values(fontNameMap).sort((lhs, rhs) => lhs.name.localeCompare(rhs.name));
};

const createLink = (fonts: Font[], display?: GoogleFontDisplay): GoogleFontLinkElement => {
    if (!fonts?.length) {
        return null;
    }

    const families = fonts
        .reduce(
            (acc, { name, weights }) => [...acc, name + (weights.size ? `:${[...weights].sort().join(",")}` : "")],
            [] as string[]
        )
        .join("|");

    const link = document.createElement("link");
    link.rel = "stylesheet";

    // can't use url search params, as google apis don't work with url encoded '+'
    link.href = `https://fonts.googleapis.com/css?family=${families}${!display ? "" : `&display=${display}`}`;

    return link;
};

export const GoogleFontLoader: FC<GoogleFontLoaderProps> = ({ fonts: googleFonts, display }) => {
    const [link, setLink] = useState<GoogleFontLinkElement>(null);
    const fonts = useMemo(() => mergedFonts(googleFonts), [googleFonts]);
    const fontsDependency = useMemo(() => stringify(fonts), [fonts]);

    // we disable dependency check, as we are hashing fonts into string to avoid reloads of fonts, of which there could be a huge amount
    useEffect(() => setLink(createLink(fonts, display)), [fontsDependency, display]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const callIfLink = (fn: (link: HTMLLinkElement) => void) => {
            if (link) {
                fn(link);
            }
        };

        const head = document.head;
        callIfLink((link) => head.appendChild(link));

        return () => callIfLink((link) => head.removeChild(link));
    }, [link]);

    return null;
};
